import React, { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateFieldVerificationSize } from "services";

import { GET_FIELD_VERIFICATIONS, UPDATE_FIELD_VERIFICATION_SIZE } from "consts";

import { Button, Col, Form, InputNumber, Modal, Row, Typography, Message as message } from "components";

export const FieldVerificationSizeForm = ({
  verificationId,
  size,
  farmId,
  onSuccess,
}: {
  verificationId: string;
  size: number;
  farmId: string;
  onSuccess: () => void;
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSize, setPendingSize] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationKey: [UPDATE_FIELD_VERIFICATION_SIZE],
    mutationFn: updateFieldVerificationSize,
    onSuccess: () => {
      message({
        content: "Field verification size updated",
        icon: "gremlin",
      });

      // Invalidate field verifications query to update the field list and crop list
      // (both use GET_FIELD_VERIFICATIONS - field list directly, crop list via PracticeList)
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS, farmId] });

      setShowConfirmModal(false);
      onSuccess();
    },
  });

  const handleSubmit = (v: { size: number }) => {
    setPendingSize(v.size);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (pendingSize !== null) {
      mutate({
        verificationId,
        size: pendingSize,
      });
    }
  };

  return (
    <>
      <Form onFinish={handleSubmit}>
        <Row align="middle">
          <Col md={18}>
            <InputNumber name="size" label="Field verification size" rules={[{ required: true }]} min={0} initialValue={size} />
          </Col>

          <Col md={6}>
            <Button loading={isPending} type="primary" htmlType="submit">
              Save
            </Button>
          </Col>
        </Row>
      </Form>
      <Modal
        title="Confirm size change"
        showModal={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onOk={handleConfirm}
        okText="Yes, update size"
        cancelText="Cancel"
        content={
          <>
            <Typography variant="paragraph-m" bold>
              Are you sure?
            </Typography>
            <Typography variant="paragraph-m">
              You are about to change the field verification size from <strong>{size} ha</strong> to <strong>{pendingSize} ha</strong>.
            </Typography>
          </>
        }
      />
    </>
  );
};
